import * as LaunchDarkly from 'launchdarkly-js-client-sdk'
import type { App as VueApp, Plugin as VuePlugin, InjectionKey } from 'vue'

const launchDarklyClientId = import.meta.env.VITE_OUTPUT_LAUNCHDARKLY_CLIENT_ID

type LaunchDarklyClient = {
  plugin: VuePlugin
  client: LaunchDarkly.LDClient
}

export const launchDarklyInjectionKey: InjectionKey<LaunchDarkly.LDClient> = Symbol('launchDarkly')

// Export a module-level variable to hold the client instance
let ldClientInstance: LaunchDarkly.LDClient | null = null

export const getLDClient = () => ldClientInstance

export const createLaunchDarkly = (): LaunchDarklyClient => {
  // Set anonymous user context for LaunchDarkly
  const context = {
    kind: 'multi',
    anonymous_user: {
      kind: 'anonymous_user',
      anonymous: true
    }
  }

  const client = LaunchDarkly.initialize(launchDarklyClientId, context)

  // Store the client instance for module-level access
  ldClientInstance = client

  // Initialize in the background so first paint is never blocked on LD
  // (or its 5s timeout). Flag values stream in and update reactively. WEB-7778
  // Fail quietly for users: a failed init just means default flag values.
  // Surface it outside production for local/staging debugging only.
  client.waitForInitialization(5000).catch(error => {
    if (import.meta.env.VITE_OUTPUT_ENVIRONMENT !== 'production') {
      console.error('Error initializing LaunchDarkly:', error)
    }
  })

  const plugin = {
    install: (app: VueApp) => {
      app.provide(launchDarklyInjectionKey, client)
      app.config.globalProperties.$ldClient = client
    }
  } as VuePlugin

  return {
    plugin,
    client
  }
}
